import { fetcher, fetcherSession, Session } from 'fetcher-session';
import {
  BuyerCancellationRefundMethod,
  RailsDisputeReason,
  SwapSellerQuickCancelReason,
} from '~/api/swapDetails/types';
import { RailsImage, RailsShipping } from '~/typings/services/rails/item';
import { RailsSwapDetail } from '~/typings/services/rails/swaps';

export const fetchSwapById = (
  session: Session | undefined,
  id: number | string,
) => fetcherSession<RailsSwapDetail>(session, `/v1/swaps/${id}`);

export const fetchSwapPing = (id: number) =>
  fetcher<{ updated_at: string }>(`/v1/swaps/${id}/ping`);

export const fetchSwapReportReasons = () =>
  fetcher<string[]>('/v1/swaps/reports/reasons');

export const postReportSwap = (
  swapId: number,
  reason: string,
  description: string,
) =>
  fetcher(`/v1/swaps/${swapId}/report`, {
    method: 'post',
    body: {
      reason,
      message: description,
    },
  });

export const putMarkSwapAsRead = (swapId: number) =>
  fetcher<RailsSwapDetail>(`/v1/swaps/${swapId}/read`, {
    method: 'put',
  });

export const putMarkSwapAsUnread = (swapId: number) =>
  fetcher<RailsSwapDetail>(`/v1/swaps/${swapId}/unread`, {
    method: 'put',
  });

export const putArchiveSwap = (swapId: number) =>
  fetcher(`/v1/swaps/${swapId}/archive`, {
    method: 'put',
  });

export const putUnarchiveSwap = (swapId: number) =>
  fetcher(`/v1/swaps/${swapId}/unarchive`, {
    method: 'put',
  });

export const postSendMessage = (id: number | string, message: string) =>
  fetcher<RailsSwapDetail>(`/v1/swaps/${id}/messages`, {
    method: 'post',
    body: {
      message: {
        body: message,
      },
    },
  });

export const putSwapAcceptOffer = (swapId: number | string, offerId: number) =>
  fetcher<RailsSwapDetail>(`/v1/swaps/${swapId}/offers/${offerId}/accept`, {
    method: 'put',
  });

export const putSwapRejectOffer = (swapId: number | string, offerId: number) =>
  fetcher<RailsSwapDetail>(`/v1/swaps/${swapId}/offers/${offerId}/reject`, {
    method: 'put',
  });

export const putSwapRejectSellerOffer = (
  swapId: number | string,
  offerId: number,
) =>
  fetcher<RailsSwapDetail>(
    `/v1/swaps/${swapId}/seller_offers/${offerId}/reject`,
    {
      method: 'put',
    },
  );

export const putSwapWithdrawOffer = (
  swapId: number | string,
  offerId: number,
) =>
  fetcher<RailsSwapDetail>(`/v1/swaps/${swapId}/offers/${offerId}/withdraw`, {
    method: 'put',
  });

export const putSwapWithdrawCounterOffer = (
  swapId: number | string,
  offerId: number,
) =>
  fetcher<RailsSwapDetail>(
    `/v1/swaps/${swapId}/counter_offers/${offerId}/withdraw`,
    {
      method: 'put',
    },
  );

export const putSwapWithdrawSellerOffer = (
  swapId: number | string,
  offerId: number,
) =>
  fetcher<RailsSwapDetail>(
    `/v1/swaps/${swapId}/seller_offers/${offerId}/withdraw`,
    {
      method: 'put',
    },
  );

export const postMakeSellerOffer = (
  swapId: number | string,
  offerAmount: number,
  variationId?: number | string,
  message?: string,
) =>
  fetcher(`/v1/swaps/${swapId}/seller_offers`, {
    method: 'post',
    body: {
      offer: {
        message,
        item_variation_id: variationId,
        amount: offerAmount,
      },
    },
  });

export const postMakeCounterOffer = (
  swapId: number | string,
  offerAmount: number,
  variationId?: number | string,
  message?: string,
) =>
  fetcher(`/v1/swaps/${swapId}/counter_offers`, {
    method: 'post',
    body: {
      offer: {
        message,
        item_variation_id: variationId,
        amount: offerAmount,
      },
    },
  });

export const putSwapRejectCounterOffer = (
  swapId: number | string,
  offerId: number,
) =>
  fetcher<RailsSwapDetail>(
    `/v1/swaps/${swapId}/counter_offers/${offerId}/reject`,
    {
      method: 'put',
    },
  );

export const putLeaveSwapFeedback = (
  swapId: number,
  feeling: string,
  description: string,
) =>
  fetcher(`/v1/swaps/${swapId}/feedback`, {
    method: 'put',
    body: {
      body: description,
      rating: feeling,
    },
  });

export const putSwapCancel = (
  swapId: number,
  refundMethod: BuyerCancellationRefundMethod,
) =>
  fetcher(`/v1/swaps/${swapId}/cancel`, {
    method: 'put',
    body: {
      refund_strategy: refundMethod,
    },
  });

export const deleteCloseDispute = (swapId: number) =>
  fetcher(`/v2/swaps/${swapId}/dispute`, {
    method: 'delete',
  });

export const putUpdateLabel = (swapId: number) =>
  fetcher(`/v1/swaps/${swapId}/label`, {
    method: 'put',
  });

export const getOfflineTrackingCarriers = (
  shipFromCountry?: string,
  shipToCountry?: string,
) =>
  fetcher<{
    carriers: { token: string; display: string }[];
  }>('/commerce/ship/v1/meta/supported_carriers', {
    params: {
      ship_from_country: shipFromCountry,
      ship_to_country: shipToCountry,
    },
  });

export const putCustomLabelTracking = (
  swapId: number,
  carrier: string,
  trackingNumber: string,
) =>
  fetcher(`/v1/swaps/${swapId}/tracking_label`, {
    method: 'put',
    body: {
      shipment: {
        carrier,
        tracking_number: trackingNumber,
      },
    },
  });

export const putOfflineTracking = (
  swapId: number,
  carrier: string,
  trackingNumber: string,
) =>
  fetcher(`/v1/swaps/${swapId}/offline_tracking_label`, {
    method: 'put',
    body: {
      shipment: {
        carrier,
        tracking_number: trackingNumber,
      },
    },
  });

export const fetchSellerQuickCancelReasons = (swapId: number) =>
  fetcher<SwapSellerQuickCancelReason[]>(`/v1/swaps/${swapId}/cancel_reasons`);

export const putSellerQuickCancel = (swapId: number, reason: string) =>
  fetcher(`/v1/swaps/${swapId}/seller_quick_cancel`, {
    method: 'put',
    body: {
      reason,
    },
  });

export const putMarkSwapAsReceived = (swapId: number) =>
  fetcher(`/v1/swaps/${swapId}/receive`, {
    method: 'put',
  });

export const putAcceptSwap = (swapId: number) =>
  fetcher(`/v1/swaps/${swapId}/accept`, {
    method: 'put',
  });

export const postUploadDisputePhoto = (swapId: number, photo: File | Blob) => {
  const formData = new FormData();
  formData.append('image', photo);

  return fetcher<RailsImage>(`/v2/swaps/${swapId}/dispute/images`, {
    method: 'post',
    blob: true,
    body: formData,
  });
};

export const postOpenDispute = (
  swapId: number,
  disputeReason: RailsDisputeReason,
  description: string,
  photoIds?: number[],
) =>
  fetcher(`/v2/swaps/${swapId}/dispute`, {
    method: 'post',
    body: {
      dispute: {
        description,
        reason: disputeReason,
        image_ids: photoIds,
      },
    },
  });

export const postIssuePartialRefund = (swapId: number, amount: number) =>
  fetcher(`/v1/swaps/${swapId}/partial_refund`, {
    method: 'post',
    body: {
      amount,
    },
  });

export const putSyncTracking = (swapId: number) =>
  fetcher(`/v1/swaps/${swapId}/sync_label`, {
    method: 'post',
    body: {},
  });

export const fetchSwapDetailsReturnShippingCost = (swapId: number) =>
  fetcher<RailsShipping>(`/v2/swaps/${swapId}/dispute/return_shipping`);

export const postApproveReturn = (swapId: number) =>
  fetcher(`/v2/swaps/${swapId}/dispute/return_shipping`, {
    method: 'post',
  });
